import React from 'react';
import { Combobox } from '@jobber/components/Combobox';

const isComboboxValue = (value) =>
	(typeof value === 'string' || typeof value === 'number') && String(value).trim() !== '';

/**
 * Jobber's Combobox calls `toString()` on every selected and option id.
 * Normalize data at this boundary so incomplete API/localStorage values cannot
 * throw while the menu is rendering.
 */
export function normalizeComboboxOption(option) {
	if (isComboboxValue(option)) {
		return { id: option, label: String(option) };
	}

	if (!option || typeof option !== 'object') return null;

	const id = option.id ?? option._id ?? option.value ?? option.label;
	const label = option.label ?? option.name ?? option.title ?? id;

	if (!isComboboxValue(id) || !isComboboxValue(label)) return null;

	return {
		...option,
		id,
		label: String(label)
	};
}

export function normalizeComboboxSelection(selection) {
	if (!Array.isArray(selection)) return [];
	return selection.map(normalizeComboboxOption).filter(Boolean);
}

function normalizeChildren(children) {
	return React.Children.toArray(children).flatMap((child) => {
		if (!React.isValidElement(child) || child.type !== Combobox.Option) return [child];

		const option = normalizeComboboxOption(child.props);
		return option ? [React.cloneElement(child, option)] : [];
	});
}

/**
 * Drop-in replacement for Jobber's Combobox with defensive option handling.
 */
export function SafeCombobox({ selected, onSelect, children, ...props }) {
	return (
		<Combobox
			{...props}
			selected={normalizeComboboxSelection(selected)}
			onSelect={(selection) => onSelect?.(normalizeComboboxSelection(selection))}
		>
			{normalizeChildren(children)}
		</Combobox>
	);
}

SafeCombobox.Activator = Combobox.Activator;
SafeCombobox.Action = Combobox.Action;
SafeCombobox.Option = Combobox.Option;
